import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Crown, Lock, ShieldQuestion, Users, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECURITY_QUESTIONS = [
  "What was the name of your first pet?",
  "What city were you born in?",
  "What is your mother's maiden name?",
  "What was the name of your first school?",
  "What is your favorite sports team?",
];

export default function InviteAccept() {
  const { inviteId } = useParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [securityQuestion, setSecurityQuestion] = useState(SECURITY_QUESTIONS[0]);
  const [securityAnswer, setSecurityAnswer] = useState("");

  const fetchInvite = useCallback(async () => {
    try {
      const res = await fetch(`${API}/invites/${inviteId}`);
      let data;
      try {
        const clone = res.clone();
        try {
          data = await res.json();
        } catch {
          // Body may be disturbed/locked — try the clone
          data = await clone.json();
        }
      } catch {
        throw new Error("This invite is invalid or has expired");
      }
      if (!res.ok) throw new Error(data.detail || "This invite is invalid or has expired");
      setInvite(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [inviteId]);

  useEffect(() => {
    fetchInvite();
  }, [inviteId, fetchInvite]);

  const handleAccept = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setError("Email is required");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    if (!securityAnswer.trim()) {
      setError("Please answer the security question");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const res = await fetch(`${API}/invites/${inviteId}/accept`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          invite_id: inviteId,
          email: email.trim(),
          password,
          security_question: securityQuestion,
          security_answer: securityAnswer,
        }),
      });
      let data;
      try {
        const clone = res.clone();
        try { data = await res.json(); } catch { data = await clone.json(); }
      } catch { throw new Error("Failed to process response. Please try again."); }
      if (!res.ok) throw new Error(data.detail || "Failed to accept invite");
      login(data.user);
      navigate("/dashboard");
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-2 mb-8">
          <Crown className="w-10 h-10 text-emerald-600" />
          <span className="text-3xl font-bold text-white font-heading italic">WarMap</span>
        </div>

        <Card className="bg-[#1A1A1A] border-white/10">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl text-white font-heading">Join Your Team</CardTitle>
            <CardDescription className="text-gray-400">
              You've been invited to join WarMap
            </CardDescription>
          </CardHeader>
          <CardContent>
            {error && (
              <div className="mb-4 p-3 rounded-md bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                {error}
              </div>
            )}

            {loading ? (
              <div className="text-center py-8 text-gray-400">Loading invite...</div>
            ) : !invite ? (
              <div className="text-center py-8">
                <p className="text-gray-400 mb-4">This invite is invalid or has expired.</p>
                <Link to="/login">
                  <Button className="bg-gradient-to-r from-emerald-800 to-emerald-600">Go to Login</Button>
                </Link>
              </div>
            ) : (
              <>
                <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-4 mb-6">
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="w-4 h-4 text-emerald-400" />
                    <span className="text-sm font-medium text-emerald-300">{invite.account_name}</span>
                  </div>
                  <p className="text-sm text-gray-300">
                    Welcome, <span className="text-white font-medium">{invite.name}</span>! You're joining as a{" "}
                    <span className="text-emerald-400 capitalize">{invite.role}</span>.
                  </p>
                </div>

                <form onSubmit={handleAccept} className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-gray-300">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                      <Input
                        data-testid="invite-email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Create a username"
                        className="pl-10 bg-white/5 border-white/10 text-white"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-gray-300">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                      <Input
                        data-testid="invite-password"
                        type="password"
                        placeholder="Create a password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="pl-10 bg-white/5 border-white/10 text-white"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-gray-300">Confirm Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                      <Input
                        data-testid="invite-confirm-password"
                        type="password"
                        placeholder="Confirm password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="pl-10 bg-white/5 border-white/10 text-white"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-gray-300 flex items-center gap-1">
                      <ShieldQuestion className="w-3 h-3" /> Security Question
                    </Label>
                    <Select value={securityQuestion} onValueChange={setSecurityQuestion}>
                      <SelectTrigger data-testid="invite-security-question" className="bg-white/5 border-white/10 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1A1A1A] border-white/10">
                        {SECURITY_QUESTIONS.map((q) => (
                          <SelectItem key={q} value={q} className="text-white">{q}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-gray-300">Security Answer</Label>
                    <Input
                      data-testid="invite-security-answer"
                      type="text"
                      placeholder="Your answer"
                      value={securityAnswer}
                      onChange={(e) => setSecurityAnswer(e.target.value)}
                      className="bg-white/5 border-white/10 text-white"
                      required
                    />
                  </div>

                  <Button
                    type="submit"
                    data-testid="invite-accept-btn"
                    disabled={submitting}
                    className="w-full bg-gradient-to-r from-emerald-800 to-emerald-600"
                  >
                    {submitting ? "Joining..." : "Join Team"}
                  </Button>
                </form>
              </>
            )}

            <div className="mt-4 text-center">
              <Link to="/login" className="text-sm text-emerald-400 hover:text-emerald-300">
                Already have an account? Sign in
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
